<template>
  <div class="results-container v-card" :style="{ borderLeft: `6px solid ${getStatusLabel(status).color}` }">
    <div class="campaign-header">
      <div class="campaign-info">
        <h1 class="campaign-title">
          <span> {{ title }} </span>
          <mf-tooltip v-if="isFlashSales" bottom>
            <template #activator="{ on }">
              <span> {{ flashIcon }} </span>
            </template>
            <span>Oferta relâmpago.</span>
          </mf-tooltip>
        </h1>
        <span class="campaign-description"> {{ description }} </span>
        <div class="campaign-status">
          <mf-icon fill small :color="getStatusLabel(status).color">fiber_manual_record</mf-icon>
          <span>{{ getStatusLabel(status).text }}</span>
        </div>
      </div>
      <div v-if="showSettingsButton" class="d-flex">
        <mf-button :label="settingsButtonLabel" outlined text-transform="none" class="offer-settings-button" @click="emitSettingsFunction" />
      </div>
    </div>

    <div class="campaign-body mt-8">
      <div :class="{ 'campaign-info-cards': !isIndustry, 'industry-campaign-info-cards': isIndustry }">
        <div v-for="card in cardsItems" :key="card.key" :class="{ 'info-card': true, 'info-card-industry': isIndustry }">
          <info-card
            :title="card.title"
            :description="card.value"
            :tooltip="card.tooltip"
            :card-key="card.key"
            :style="{ height: isIndustry ? '116px' : 'unset' }"
          >
            <template v-if="card.key === 'target'" slot="additional-content">
              <div v-if="campaign.segments && campaign.segments.length">
                <v-chip-group :column="showAllSegments" show-arrows active-class="primary--text">
                  <v-chip v-for="(segment, index) in campaign.segments" :key="index" color="white" small :ripple="false" @click="goToSegmentation(index)">
                    <span v-if="segment">
                      <b>{{ segment.name }}</b>
                      {{ segment.clients_qty_at_campaign !== null ? ` (${segment.clients_qty_at_campaign} clientes)` : '' }}
                    </span>
                  </v-chip>
                </v-chip-group>
                <a v-if="campaign.segments.length > 1" class="user-select-none mt-3 mb-0 body-2" @click="showAllSegments = !showAllSegments">
                  {{ showAllSegments ? 'Minimizar' : 'Mostrar todos' }}
                </a>
                <export-client-button :segment="campaign.segments[0]" small use-link />
              </div>
              <div v-else class="no-segments-text">
                <span>{{ isIndustry ? 'Massivo' : 'Não segmentada' }}</span>
              </div>
            </template>
          </info-card>
        </div>

        <info-card v-if="isIndustry" class="half-width" title="Lojas participantes" :description="infoCardUnitsDescription" card-key="units">
          <template #additional-content>
            <chip-list
              variant="units"
              details-type="dialog"
              dialog-title="Lojas participantes"
              :items="campaignUnits"
              :units="unitsList"
              use-default-headers
              only-dialog
            />
          </template>
        </info-card>

        <info-card v-if="isIndustry" class="half-width" title="Produtos" :description="infoCardProductsDescription" card-key="products">
          <template #additional-content>
            <chip-list
              variant="products"
              details-type="dialog"
              dialog-title="Produtos"
              :items="campaignProducts"
              :products="listAllProducts"
              use-default-headers
              only-dialog
            />
          </template>
        </info-card>
      </div>

      <div v-if="!isIndustry" class="campaign-lists">
        <chip-list
          variant="units"
          details-type="dialog"
          title="Lojas participantes:"
          dialog-title="Lojas participantes"
          :items="campaignUnits"
          :units="unitsList"
          use-default-headers
        />
        <chip-list
          v-if="!banner"
          variant="products"
          details-type="dialog"
          title="Produtos:"
          dialog-title="Produtos"
          :items="campaignProducts"
          :products="listAllProducts"
          use-default-headers
        />

        <div v-if="banner" class="banner-info-container">
          <div class="banner-info-item">
            <span class="banner-info-label">Redirecionamento:</span>
            <span
              v-if="campaign && campaign.action && (campaign.action.type === 'VIEW_FLYER' || campaign.action.type === 'VIEW_SALES')"
              class="redirect-link"
              @click="redirectToResults(campaign.action.reference_id, true, campaign.action.type)"
            >
              {{ referenceTitle || getRedirectTypeLabel(campaign) }}
            </span>
            <a
              v-else-if="campaign && campaign.action && campaign.action.type === 'EXTERNAL_LINK' && campaign.action.link"
              :href="campaign.action.link"
              target="_blank"
              rel="noopener noreferrer"
              class="redirect-link"
            >
              {{ referenceTitle || getRedirectTypeLabel(campaign) }}
            </a>
            <span v-else class="redirect-placeholder">
              {{ referenceTitle || getRedirectTypeLabel(campaign) }}
            </span>
          </div>

          <div class="banner-info-item">
            <span class="banner-info-label">Link da imagem do Banner:</span>
            <a v-if="campaign && campaign.image_url" :href="campaign.image_url" target="_blank" rel="noopener noreferrer" class="redirect-link"> Ver imagem </a>
            <span v-else class="redirect-placeholder">Sem imagem</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { DateTime } from 'luxon'
import { getStatusLabel, assertArray } from '@/config/helpers.js'
import { getProductFormatTypeLabel } from '@/modules/mobile/ofertas/helpers/productType.js'
import InfoCard from './InfoCard.vue'
import ChipList from '../ChipList.vue'
import ExportClientButton from '../../modules/clientes/segmentation/views/components/ExportClientButton.vue'
import { mergeAllIndividualAndGroupProductsByOffer, mergeAllComboProducts } from '@/modules/campanhas/gestao-ofertas-v2/helpers/products.js'
import { hasPostCommunication, getPostCommunicationType } from '@/modules/industria/helpers/postCommunication.js'
import { QUERY_SALES_CAMPAIGN_STATUS, QUERY_FLYER_STATUS } from '@/modules/mobile/banners/graphql/index.js'

export default {
  name: 'ResultsHeader',
  components: {
    InfoCard,
    ExportClientButton,
    ChipList,
  },
  props: {
    campaign: {
      type: Object,
      required: true,
    },
    showSettingsButton: {
      type: Boolean,
      default: true,
    },
    settingsButtonLabel: {
      type: String,
      default: 'Ver configurações da oferta',
    },
    highlight: {
      type: Boolean,
      default: false,
    },
    banner: {
      type: Boolean,
      default: false,
    },
    offerType: {
      type: String,
      validator: (value) =>
        [
          'general',
          'percentage',
          'dynamic_pricing_percentage',
          'takepay',
          'combo',
          'cashback_percentage',
          'cashback_absolute',
          'points',
          'nextbuycashback',
          'dynamic_rule',
          'crosssell',
          'benefits',
        ].includes(value),
    },
  },
  data: () => ({
    flashIcon: '⚡',
    showAllSegments: false,
    showAllProducts: false,
    showAllUnits: false,
    referenceTitle: '',
  }),
  computed: {
    title() {
      return this.campaign.name
    },
    description() {
      return this.campaign.description
    },
    status() {
      return this.campaign.status.toUpperCase()
    },
    validityPeriodInitialDate() {
      const initialDate = DateTime.fromFormat(this.campaign.start_date.toString(), 'yyyy-MM-dd').toFormat('dd/MM/yyyy')
      const initialTime = this.campaign.flash ? ' ' + DateTime.fromFormat(this.campaign?.start_time, 'HH:mm:ss').toFormat('HH:mm') : ''
      return initialDate + initialTime
    },
    validityPeriodFinalDate() {
      const finalDate = DateTime.fromFormat(this.campaign.end_date.toString(), 'yyyy-MM-dd').toFormat('dd/MM/yyyy')
      const finalTime = this.campaign.flash ? ' ' + DateTime.fromFormat(this.campaign?.end_time, 'HH:mm:ss').toFormat('HH:mm') : ''
      return finalDate + finalTime
    },
    cardsItems() {
      if (this.banner) {
        return [
          {
            key: 'validity-period',
            title: 'Período de validade',
            value: `${this.validityPeriodInitialDate} - ${this.validityPeriodFinalDate}`,
            tooltip: '',
            hasSlot: false,
          },
          {
            key: 'campaign-template',
            title: 'Prioridade',
            value: this.getPriorityLabel(this.campaign.priority),
            tooltip: '',
            hasSlot: false,
          },
        ]
      }

      let items = [
        {
          key: 'validity-period',
          title: 'Período de validade',
          value: `${this.validityPeriodInitialDate} - ${this.validityPeriodFinalDate}`,
          tooltip: '',
          hasSlot: false,
        },
        ...(this.isIndustry
          ? [
              {
                key: 'campaign-industry',
                title: 'Indústria',
                value: this.campaign.industry.name,
                tooltip: '',
                hasSlot: false,
              },
            ]
          : [
              {
                key: 'campaign-template',
                title: `Modelo de ${this.highlight ? 'destaque' : 'campanha'}`,
                value: this.campaign.activatable ? 'Expandido' : 'Padrão',
                tooltip: '',
                hasSlot: false,
              },
            ]),
        ...(this.isMercapromo && this.hasPostCommunication(this.campaign)
          ? [
              {
                key: 'communication',
                title: 'Comunicação',
                value: this.getPostCommunicationType(this.campaign),
                tooltip: '',
                hasSlot: false,
              },
            ]
          : []),
      ]

      if (!this.highlight) {
        items.push({
          key: 'campaign-format',
          title: 'Formato',
          value: this.getOfferType,
          tooltip: '',
          hasSlot: false,
        })
      }

      if (this.isIndustry && this.isCashback) {
        items.push({
          key: 'cashback-expiration-date',
          title: 'Validade do Cashback',
          value: this.getCashbackExpirationDate,
          tooltip: '',
          hasSlot: false,
        })
      }

      if (this.offerType === 'dynamic_rule') {
        items.push({
          key: 'voucher-type',
          title: 'Tipo do Voucher',
          value: this.formatVoucher(this.campaign?.dynamic_rule_offer_sales?.[0]?.benefits?.type),
          tooltip: '',
          hasSlot: false,
        })
      } else {
        items.push({
          key: 'target',
          title: 'Público alvo',
          value: '',
          tooltip:
            this.campaign.segments && this.campaign.segments.length
              ? 'Clique em cima do segmento e uma nova aba será aberta para visualizar os critérios da segmentação.'
              : '',
          hasSlot: true,
        })
      }

      return items
    },
    isIndustry() {
      return Boolean(this.campaign.industry)
    },
    isMercapromo() {
      return Boolean(this.campaign?.industry?.is_mercapromo)
    },
    getOfferType() {
      if (!this.campaign.types) return '-'
      return this.campaign.types.length > 1 ? 'Múltiplos' : getProductFormatTypeLabel(this.offerType)
    },
    unitsList() {
      return this.campaign.units.map((unit) => {
        return { unit: unit.name }
      })
    },
    listAllProducts() {
      const offerSalesProducts = mergeAllIndividualAndGroupProductsByOffer(this.campaign.offer_sales, 'offer_sales')
      const percentageOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(this.campaign.percentage_offer_sales, 'percentage_offer_sales')
      const dynamicPricingPercentageOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(
        this.campaign.dynamic_pricing_percentage_offer_sales,
        'dynamic_pricing_percentage_offer_sales'
      )
      const takeAndPayOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(this.campaign.takepay_offer_sales, 'takepay_offer_sales')
      const cashbackPercentageOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(
        this.campaign.cashback_percentage_offer_sales,
        'cashback_percentage_offer_sales'
      )
      const cashbackAbsoluteOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(
        this.campaign.cashback_absolute_offer_sales,
        'cashback_absolute_offer_sales'
      )
      const pointsOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(this.campaign.points_offer_sales, 'points_offer_sales')

      const comboOfferSalesProducts = assertArray(this.campaign.combo_offer_sales).reduce((productList, product) => {
        productList.push(...mergeAllComboProducts(product.target_products, 'target_products'))
        productList.push(...mergeAllComboProducts(product.trigger_products, 'trigger_products'))

        return productList.map((product) => {
          return { name: product.name, image: product?.image_url, sku: product.sku }
        })
      }, [])

      const nextBuyCashbackOfferSalesProducts = this.campaign.next_buy_cashback_offer_sales || []

      let dynamicRuleOfferSalesProducts = []
      if (this.campaign?.dynamic_rule_offer_sales?.length > 0) {
        this.campaign.dynamic_rule_offer_sales.forEach((dynamic_rule) => {
          dynamicRuleOfferSalesProducts.push(dynamic_rule.trigger_products[0])
        })
      }

      let crossSellOfferSalesProducts = []
      if (this.campaign?.cross_sell_offer_sales?.length > 0) {
        this.campaign.cross_sell_offer_sales.forEach((cross_sell) => {
          cross_sell.target_products.forEach((target_product) => {
            crossSellOfferSalesProducts.push(target_product)
          })
          cross_sell.trigger_products.forEach((trigger_product) => {
            crossSellOfferSalesProducts.push(trigger_product)
          })
        })
      }

      let benefitsOfferSalesProducts = []
      if (this.campaign?.benefits_offer_sales?.length > 0) {
        this.campaign.benefits_offer_sales.forEach((benefit) => {
          benefit.trigger_products.forEach((trigger_product) => {
            benefitsOfferSalesProducts.push(trigger_product)
          })
        })
      }

      const productsArray = [
        ...offerSalesProducts,
        ...percentageOfferSalesProducts,
        ...dynamicPricingPercentageOfferSalesProducts,
        ...takeAndPayOfferSalesProducts,
        ...comboOfferSalesProducts,
        ...cashbackPercentageOfferSalesProducts,
        ...cashbackAbsoluteOfferSalesProducts,
        ...pointsOfferSalesProducts,
        ...nextBuyCashbackOfferSalesProducts,
        ...dynamicRuleOfferSalesProducts,
        ...crossSellOfferSalesProducts,
        ...benefitsOfferSalesProducts,
      ]

      return productsArray.map((product) => {
        return { name: product.name, image: product?.image_url, sku: product.sku }
      })
    },
    campaignProducts() {
      return this.listAllProducts.map((product) => `${product.sku} - ${product.name}`)
    },
    campaignUnits() {
      return this.campaign.units.map((unit) => unit.name)
    },
    infoCardUnitsDescription() {
      const units = this.unitsList?.length
      let text = 'selecionada'
      if (units) {
        if (units > 1) {
          text = 'selecionadas'
        }
        return `${units} ${text}`
      }
      return 'Nenhuma loja'
    },
    infoCardProductsDescription() {
      const products = this.listAllProducts?.length
      let text = 'selecionado'
      if (products) {
        if (products > 1) {
          text = 'selecionados'
        }
        return `${products} ${text}`
      }
      return 'Nenhum produto'
    },
    isCashback() {
      return this.offerType === 'nextbuycashback' || this.offerType === 'cashback_absolute' || this.offerType === 'cashback_percentage'
    },
    getCashbackExpirationDate() {
      const { next_buy_cashback_offer_sales, cashback_absolute_offer_sales, cashback_percentage_offer_sales } = this.campaign
      if (next_buy_cashback_offer_sales?.[0]?.cashback_expiration_date) {
        return DateTime.fromISO(next_buy_cashback_offer_sales?.[0]?.cashback_expiration_date).toFormat('dd/MM/yyyy')
      } else if (cashback_absolute_offer_sales?.[0]?.expiration_cashback?.expires_at) {
        return DateTime.fromISO(cashback_absolute_offer_sales?.[0]?.expiration_cashback?.expires_at).toFormat('dd/MM/yyyy')
      } else if (cashback_percentage_offer_sales?.[0]?.expiration_cashback?.expires_at) {
        return DateTime.fromISO(cashback_percentage_offer_sales?.[0]?.expiration_cashback?.expires_at).toFormat('dd/MM/yyyy')
      }
      return '-'
    },
    isFlashSales() {
      return this.campaign?.flash
    },
  },
  mounted() {
    this.loadReferenceTitle().finally(() => {
      this.$emit('ready')
    })
  },
  methods: {
    getStatusLabel,

    hasPostCommunication,
    getPostCommunicationType,

    formatVoucher(voucher) {
      return voucher ? voucher[0].toUpperCase() + voucher.substr(1).toLowerCase() : ''
    },
    async loadReferenceTitle() {
      const action = this.campaign?.action
      if (!action || !action.reference_id || !action.type) return

      try {
        if (action.type === 'VIEW_FLYER') {
          const { data } = await this.$apollo.query({
            query: QUERY_FLYER_STATUS,
            variables: { _id: action.reference_id },
            fetchPolicy: 'no-cache',
          })
          this.referenceTitle = data?.flyer?.title || ''
        } else if (action.type === 'VIEW_SALES') {
          const { data } = await this.$apollo.query({
            query: QUERY_SALES_CAMPAIGN_STATUS,
            variables: { id: action.reference_id },
            fetchPolicy: 'no-cache',
          })
          this.referenceTitle = data?.offerSchedule?.name || ''
        }
      } catch (error) {
        console.error('Erro ao buscar título da referência:', error)
      }
    },
    getPriorityLabel(priority) {
      switch (priority) {
        case 1:
          return 'Baixa'
        case 2:
          return 'Média'
        case 3:
          return 'Alta'
        default:
          return '-'
      }
    },
    getRedirectTypeLabel(item) {
      if (!item.action.link) return 'Sem link'

      switch (item.action.type) {
        case 'EXTERNAL_LINK':
          return 'Link externo'
        case 'INTERNAL_PAGE':
          return 'Link interno'
        case 'VIEW_FLYER':
          return 'Encarte'
        case 'VIEW_SALES':
          return 'Oferta vinculada'
        default:
          return '-'
      }
    },
    async redirectToResults(id, franchise = false, type = null) {
      if (type === 'INTERNAL_PAGE') return

      try {
        if (type === 'VIEW_FLYER') {
          const { data } = await this.$apollo.query({
            query: QUERY_FLYER_STATUS,
            variables: {
              _id: id,
              // franchise,
              // only_franchise: franchise || this.$getLoggedUser().is_franchise,
            },
            fetchPolicy: 'no-cache',
          })

          const flyer = data?.flyer
          if (!flyer || !flyer.status) throw new Error('Flyer inválido')

          const isFinished = flyer.status === 'FINISHED'
          const route = isFinished ? { name: 'encartes-resultados', params: { id } } : { name: 'encartes-edit', params: { id } }

          const href = this.$router.resolve(route).href
          window.open(href, '_blank')
        } else if (type === 'VIEW_SALES') {
          const { data } = await this.$apollo.query({
            query: QUERY_SALES_CAMPAIGN_STATUS,
            variables: {
              id,
              // franchise,
            },
            fetchPolicy: 'no-cache',
          })

          const campaign = data?.offerSchedule
          if (!campaign || !campaign.status) throw new Error('Campanha inválida')

          const isFinished = campaign.status === 'FINISHED'
          const route = isFinished
            ? { name: 'gestao-ofertas-resultado', params: { id }, query: { franchise: String(franchise) } }
            : { name: 'gestao-ofertas-editar', params: { id } }

          const href = this.$router.resolve(route).href
          window.open(href, '_blank')
        }
      } catch (error) {
        console.error('Erro ao redirecionar para resultados:', error)
        this.$snackbar('Erro ao redirecionar para a campanha.')
      }
    },
    emitSettingsFunction() {
      let route = ''
      if (this.isIndustry) {
        route = this.$router.resolve({ name: 'ofertas-trade-visualizar', params: { id: this.campaign._id } })
      } else if (this.highlight) {
        route = this.$router.resolve({ name: 'highlight-edit', params: { id: this.campaign._id } })
      } else if (this.banner) {
        route = this.$router.resolve({ name: 'banners-edit', params: { id: this.campaign._id } })
      } else {
        route = this.$router.resolve({ name: 'gestao-ofertas-editar', params: { id: this.campaign._id } })
      }
      window.open(route.href, '_blank')
    },
    goToSegmentation(index) {
      if (!this.campaign.has_segment_data) {
        this.$snackbar({ message: 'Não é possível visualizar critérios desta campanha', snackbarColor: 'error' })
        return
      }
      const segment = this.campaign.segments[index]
      if (!segment) return
      const routeData = this.$router.resolve({
        name: 'visualizar-segmentacao',
        params: {
          campaignId: this.campaign._id,
          segmentId: segment._id,
        },
      })
      window.open(routeData.href, '_blank')
    },
  },
}
</script>

<style scoped lang="scss">
.results-container {
  border-radius: 4px;
  background: white;
  padding: 20px 24px !important;

  .campaign-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    .campaign-info {
      display: flex;
      flex-direction: column;
      gap: 6px;

      .campaign-title {
        color: $cinza-d10;
        font-size: 20px;
        font-weight: 700;
        line-height: 24px;
        letter-spacing: 0.01em;
      }

      .campaign-description {
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        color: $cinza-0;
      }

      .campaign-status {
        display: flex;
        flex-direction: row;
        gap: 2px;

        span {
          color: $cinza-d10;
          font-size: 14px;
          font-weight: 400;
          line-height: 21px;
        }
      }
    }
  }

  .campaign-body {
    display: flex;
    flex-direction: row;
    gap: 40px;

    .campaign-info-cards {
      width: 40%;
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .campaign-lists {
      width: 60%;
      display: flex;
      flex-direction: column;
      gap: 40px;
    }

    .industry-campaign-info-cards {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      width: 100%;
    }

    .info-card-industry {
      width: 49%;
      height: 116px;
    }
  }
}

.no-segments-text {
  span {
    color: $cinza-d10;
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: 0em;
  }
}

::v-deep .offer-settings-button {
  .v-btn__content {
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: 0.01em;
    text-align: center;
  }
}

.border-left-status {
  border-left: 6px solid v-bind('getStatusLabel(status).color');
}

.banner-info-label {
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-style: normal;
  font-size: 18px;
  color: #4b525a;
}

.redirect-link {
  font-family: 'Lato', sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 14px;
  color: #1976d2;
  text-decoration: underline;
  cursor: pointer;
}

.redirect-placeholder {
  font-family: 'Lato', sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 14px;
  color: #666;
}

.banner-info-container {
  display: flex;
  justify-content: space-between;
  gap: 32px;
}

.banner-info-item {
  flex: 1;
}

.banner-info-label {
  display: block;
  font-weight: 600;
  color: #444;
  margin-bottom: 4px;
}

.half-width {
  width: 49%;
}
</style>
